import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type OptionType = string | { value: string; label: string };

interface MultiSelectProps {
  label: string;
  options: readonly OptionType[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select options...',
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  // Helper functions to normalize options
  const getOptionValue = (option: OptionType): string => {
    return typeof option === 'string' ? option : option.value;
  };

  const getOptionLabel = (option: OptionType): string => {
    return typeof option === 'string' ? option : option.label;
  };

  const getLabelForValue = (value: string): string => {
    const option = options.find((opt) => getOptionValue(opt) === value);
    return option ? getOptionLabel(option) : value;
  };

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between pr-12"
            >
              {selected.length > 0 ? (
                <div className="flex gap-1 flex-wrap">
                  {selected.slice(0, 2).map((item) => (
                    <span
                      key={item}
                      className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs"
                    >
                      {getLabelForValue(item)}
                    </span>
                  ))}
                  {selected.length > 2 && (
                    <span className="text-xs text-gray-500">
                      +{selected.length - 2} more
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-gray-500">{placeholder}</span>
              )}
              <ChevronDown className="absolute right-3 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
            <Command>
              <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const value = getOptionValue(option);
                    const displayLabel = getOptionLabel(option);
                    return (
                      <CommandItem
                        key={value}
                        value={displayLabel}
                        onSelect={() => handleSelect(value)}
                      >
                        <div
                          className={cn(
                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                            selected.includes(value)
                              ? 'bg-primary text-primary-foreground'
                              : 'opacity-50 [&_svg]:invisible'
                          )}
                        >
                          <Check className="h-4 w-4" />
                        </div>
                        <span>{displayLabel}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Clear button - positioned absolutely outside PopoverTrigger */}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-8 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center hover:bg-gray-100 rounded p-0.5"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
    </div>
  );
}
